import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

const container = document.getElementById("root");

// Build-time head tags are for no-JS crawlers only. Every route re-emits its
// own via Helmet, so drop the static set first — otherwise the rendered DOM
// (what Google indexes) carries two canonicals / titles that can disagree.
document.head
  .querySelectorAll(
    'title, script[data-fp-static-ld], link[rel="canonical"], link[rel="alternate"][hreflang], meta[name="description"], meta[name="keywords"], meta[property^="og:"], meta[name^="twitter:"]'
  )
  .forEach((el) => el.remove());
const tree = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// v60.102.9 — swallow HYDRATION-MISMATCH reports, and only those.
//
// Every page shipped a thrown React error #418 ("hydration failed, the server
// HTML didn't match the client"). React treats a mismatch as RECOVERABLE — it
// quietly re-renders the subtree client-side, which is correct behaviour — but
// its default onRecoverableError writes the error to console.error. Lighthouse
// counts any console exception as a Best Practices failure, which is why all
// four audits read 96 instead of 100 while the pages worked perfectly.
//
// The underlying mismatches are being removed at the source (the live-traffic,
// flag-counter, online-counter and rotator widgets now freeze on deterministic
// seed state while the build prerenders — see lib/prerender_flag.js). This
// handler is the floor beneath that work: a snapshot of a live product can
// never be guaranteed byte-identical to a render that happens hours later, so
// a mismatch must degrade to a silent, intended recovery rather than a logged
// exception. Everything that is NOT a hydration mismatch still reaches the
// console untouched, so real bugs stay visible.
const isHydrationMismatch = (error) => {
  const text = String((error && (error.message || error.toString())) || "");
  return (
    text.includes("error #418") ||
    text.includes("error #423") ||
    text.includes("error #425") ||
    /hydrat/i.test(text)
  );
};

const onRecoverableError = (error, errorInfo) => {
  if (isHydrationMismatch(error)) return;
  // eslint-disable-next-line no-console
  console.error(error, errorInfo);
};

// When the build step prerenders the route into static HTML it marks
// #root with data-prerendered="true". In that case we MUST hydrate (not
// re-render from scratch) so the existing markup is reused, the user
// sees no FOUC, and React Router doesn't fire a double Suspense fallback.
// Locally (dev / no prerender) we fall back to createRoot.
if (container?.getAttribute("data-prerendered") === "true") {
  ReactDOM.hydrateRoot(container, tree, { onRecoverableError });
} else {
  ReactDOM.createRoot(container, { onRecoverableError }).render(tree);
}
